import { useState } from 'react'
import { useDataStore } from '../../stores/dataStore'
import { useResearchStore } from '../../stores/researchStore'
import { Button, Input, Textarea, Modal } from '../ui'
import { toast } from '../ui/Toast'

interface AddCompanyModalProps {
  isOpen: boolean
  onClose: () => void
}

function extractDomain(input: string): string {
  if (!input) return ''
  // Remove protocol and path, keep just domain
  return input
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .replace(/\/.*$/, '')
    .trim()
}

export default function AddCompanyModal({ isOpen, onClose }: AddCompanyModalProps) {
  const { addCompany } = useDataStore()
  const { settings, runResearch } = useResearchStore()
  
  const [name, setName] = useState('')
  const [website, setWebsite] = useState('')
  const [industry, setIndustry] = useState('')
  const [notes, setNotes] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const resetForm = () => {
    setName('')
    setWebsite('')
    setIndustry('')
    setNotes('')
  }

  const handleClose = () => {
    resetForm()
    onClose()
  }

  const handleSubmit = async () => {
    if (!name.trim()) {
      toast('Name is required', 'error')
      return
    }

    setIsSubmitting(true)
    try {
      const company = await addCompany({
        name: name.trim(),
        domain: extractDomain(website) || undefined,
        website: website.trim() || undefined,
        industry: industry.trim() || undefined,
        notes: notes.trim() || undefined,
      })
      
      toast('Company created', 'success')
      handleClose()
      
      // Trigger research in background if enabled
      if (settings.autoResearchNewCompanies && company) {
        runResearch(company).then(() => {
          toast('Research complete', 'success')
        }).catch(() => {
          // Research errors are non-blocking
        })
      }
    } catch {
      toast('Failed to create company', 'error')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="New company"
      footer={
        <>
          <Button variant="ghost" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? 'Creating...' : 'Create company'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Input
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Acme Corp"
          autoFocus
        />
        <Input
          label="Website"
          value={website}
          onChange={(e) => setWebsite(e.target.value)}
          placeholder="https://example.com"
        />
        <Input
          label="Industry"
          value={industry}
          onChange={(e) => setIndustry(e.target.value)}
          placeholder="e.g. Technology"
        />
        <Textarea
          label="Notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Any additional notes..."
        />
      </div>
    </Modal>
  )
}
